/**
 * Service pour les données de marché en temps réel
 * Utilise CoinGecko API (gratuite) et Binance WebSocket pour les données en temps réel
 */

import type {
  Candle,
  MarketPair,
  OrderBook,
  OrderBookEntry,
  Ticker,
  Timeframe,
} from "../types/api";

// Configuration de l'API
const COINGECKO_API = "https://api.coingecko.com/api/v3";
const BINANCE_API = "https://api.binance.com/api/v3";
const BINANCE_WS = "wss://stream.binance.com:9443/ws";

/**
 * Convertit un symbole crypto en format Binance (ex: BTCUSDT)
 * Binance utilise USDT comme devise de base, pas USD
 */
const normalizeSymbol = (symbol: string): string => {
  // Remplacer USD par USDT pour Binance
  const normalized = symbol.replace("/", "").toUpperCase();
  // Si le symbole se termine par USD, le remplacer par USDT
  if (normalized.endsWith("USD") && normalized !== "USDT") {
    return normalized.replace("USD", "USDT");
  }
  return normalized;
};

/** Catégorie pour les onglets de la Vue marché (Indices, Futures, Bonds, Forex) */
export type MarketCategory = "indices" | "futures" | "bonds" | "forex";

/**
 * Récupère les paires selon la catégorie sélectionnée
 */
export const getMarketPairsByCategory = async (
  category: MarketCategory,
): Promise<MarketPair[]> => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 8000);

  try {
    if (category === "forex") {
      const symbols = ["EURUSDT", "GBPUSDT", "AUDUSDT", "BRLUSDT", "TRYUSDT"];
      const pairs: MarketPair[] = [];
      for (const s of symbols) {
        try {
          const r = await fetch(`${BINANCE_API}/ticker/24hr?symbol=${s}`, {
            signal: controller.signal,
          });
          if (!r.ok) continue;
          const d = await r.json();
          if (d.code) continue;
          const base = s.replace("USDT", "");
          pairs.push({
            symbol: `${base}/USD`,
            baseAsset: base,
            quoteAsset: "USD",
            price: parseFloat(d.lastPrice),
            change24h: parseFloat(d.priceChange),
            changePercent24h: parseFloat(d.priceChangePercent),
            volume24h: parseFloat(d.quoteVolume || 0),
          });
        } catch {
          /* skip */
        }
      }
      clearTimeout(timeoutId);
      return pairs;
    }

    if (category === "bonds") {
      const ids = [
        "tether",
        "usd-coin",
        "binance-usd",
        "dai",
        "true-usd",
        "paxos-standard",
      ];
      const r = await fetch(
        `${COINGECKO_API}/coins/markets?vs_currency=usd&ids=${ids.join(
          ",",
        )}&sparkline=false`,
        { signal: controller.signal },
      );
      clearTimeout(timeoutId);
      if (!r.ok) return [];
      const data = await r.json();
      if (!Array.isArray(data)) return [];
      return data.map((c: any) => ({
        symbol: `${(c.symbol || "").toUpperCase()}/USD`,
        baseAsset: (c.symbol || "").toUpperCase(),
        quoteAsset: "USD",
        price: c.current_price,
        change24h: c.price_change_24h || 0,
        changePercent24h: c.price_change_percentage_24h || 0,
        volume24h: c.total_volume || 0,
      }));
    }

    if (category === "futures") {
      const ids = [
        "pax-gold",
        "wrapped-bitcoin",
        "ethereum",
        "chainlink",
        "the-graph",
      ];
      const r = await fetch(
        `${COINGECKO_API}/coins/markets?vs_currency=usd&ids=${ids.join(
          ",",
        )}&sparkline=false`,
        { signal: controller.signal },
      );
      clearTimeout(timeoutId);
      if (!r.ok) return [];
      const data = await r.json();
      if (!Array.isArray(data)) return [];
      return data.map((c: any) => ({
        symbol: `${(c.symbol || "").toUpperCase()}/USD`,
        baseAsset: (c.symbol || "").toUpperCase(),
        quoteAsset: "USD",
        price: c.current_price,
        change24h: c.price_change_24h || 0,
        changePercent24h: c.price_change_percentage_24h || 0,
        volume24h: c.total_volume || 0,
      }));
    }

    clearTimeout(timeoutId);
    return getPopularPairs();
  } catch {
    clearTimeout(timeoutId);
    return getPopularPairs();
  }
};

/**
 * Récupère la liste des paires de trading populaires
 */
export const getPopularPairs = async (): Promise<MarketPair[]> => {
  let timeoutId: NodeJS.Timeout | null = null;
  try {
    // Utilisation de CoinGecko pour les données de base
    // Créer un AbortController pour le timeout
    const controller = new AbortController();
    timeoutId = setTimeout(() => controller.abort(), 5000);

    const response = await fetch(
      `${COINGECKO_API}/coins/markets?vs_currency=usd&order=market_cap_desc&per_page=20&page=1&sparkline=false`,
      { signal: controller.signal },
    );

    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }

    if (!response.ok) {
      throw new Error(`Erreur API: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();

    if (!Array.isArray(data)) {
      throw new Error("Format de données invalide");
    }

    return data.map((coin: any) => ({
      symbol: `${coin.symbol.toUpperCase()}/USD`,
      baseAsset: coin.symbol.toUpperCase(),
      quoteAsset: "USD",
      price: coin.current_price,
      change24h: coin.price_change_24h || 0,
      changePercent24h: coin.price_change_percentage_24h || 0,
      volume24h: coin.total_volume || 0,
    }));
  } catch (error) {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    // Ne pas logger l'erreur ici pour éviter le spam dans la console
    // console.error("Erreur lors du chargement des paires:", error);
    // Tentative de fallback via Binance
    try {
      const controller = new AbortController();
      const binanceTimeoutId = setTimeout(() => controller.abort(), 5000);

      try {
        const response = await fetch(`${BINANCE_API}/ticker/24hr`, {
          signal: controller.signal,
        });

        clearTimeout(binanceTimeoutId);

        if (!response.ok) {
          throw new Error(
            `Erreur API: ${response.status} ${response.statusText}`,
          );
        }
        const data = await response.json();

        const popularSymbols = [
          "BTCUSDT",
          "ETHUSDT",
          "BNBUSDT",
          "XRPUSDT",
          "SOLUSDT",
          "ADAUSDT",
          "DOGEUSDT",
          "TRXUSDT",
          "LTCUSDT",
          "MATICUSDT",
          "AVAXUSDT",
          "LINKUSDT",
        ];

        const filtered = Array.isArray(data)
          ? data.filter((item: any) => popularSymbols.includes(item.symbol))
          : [];

        if (filtered.length > 0) {
          return filtered.map((item: any) => ({
            symbol: `${item.symbol.replace("USDT", "")}/USD`,
            baseAsset: item.symbol.replace("USDT", ""),
            quoteAsset: "USD",
            price: parseFloat(item.lastPrice),
            change24h: parseFloat(item.priceChange),
            changePercent24h: parseFloat(item.priceChangePercent),
            volume24h: parseFloat(item.quoteVolume || item.volume),
          }));
        }
      } catch (fetchError) {
        clearTimeout(binanceTimeoutId);
        throw fetchError;
      }
    } catch (binanceError) {
      // Ne pas logger pour éviter le spam
      // console.error("Erreur fallback Binance:", binanceError);
    }

    // Fallback minimal si Binance échoue aussi
    return [
      {
        symbol: "BTC/USD",
        baseAsset: "BTC",
        quoteAsset: "USD",
        price: 45000,
        change24h: 500,
        changePercent24h: 1.12,
        volume24h: 25000000000,
      },
      {
        symbol: "ETH/USD",
        baseAsset: "ETH",
        quoteAsset: "USD",
        price: 2800,
        change24h: -50,
        changePercent24h: -1.75,
        volume24h: 12000000000,
      },
      {
        symbol: "BNB/USD",
        baseAsset: "BNB",
        quoteAsset: "USD",
        price: 300,
        change24h: 5,
        changePercent24h: 1.2,
        volume24h: 1000000000,
      },
      {
        symbol: "XRP/USD",
        baseAsset: "XRP",
        quoteAsset: "USD",
        price: 0.6,
        change24h: 0.01,
        changePercent24h: 1.5,
        volume24h: 500000000,
      },
    ];
  }
};

/**
 * Vérifie si un symbole existe sur Binance
 */
export const checkSymbolExists = async (symbol: string): Promise<boolean> => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 5000);

  try {
    const normalized = normalizeSymbol(symbol);
    const response = await fetch(
      `${BINANCE_API}/ticker/24hr?symbol=${normalized}`,
      {
        signal: controller.signal,
        mode: "cors",
      },
    );

    clearTimeout(timeoutId);

    if (!response.ok) {
      return false;
    }

    const data = await response.json();
    // Si Binance retourne un code d'erreur, le symbole n'existe pas
    if (data.code) {
      return false;
    }

    return true;
  } catch (error: any) {
    clearTimeout(timeoutId);
    // Ignorer silencieusement les erreurs CORS et réseau
    // Ce sont des erreurs attendues pour des symboles qui n'existent pas
    return false;
  }
};

/**
 * Récupère le ticker pour un symbole donné
 */
export const getTicker = async (symbol: string): Promise<Ticker> => {
  try {
    const normalized = normalizeSymbol(symbol);
    const response = await fetch(
      `${BINANCE_API}/ticker/24hr?symbol=${normalized}`,
    );

    if (!response.ok) {
      throw new Error(`Erreur API: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();

    // Vérifier si Binance retourne une erreur
    if (data.code) {
      throw new Error(data.msg || "Symbole non disponible sur Binance");
    }

    return {
      symbol: data.symbol,
      price: parseFloat(data.lastPrice),
      change24h: parseFloat(data.priceChange),
      changePercent24h: parseFloat(data.priceChangePercent),
      high24h: parseFloat(data.highPrice),
      low24h: parseFloat(data.lowPrice),
      volume24h: parseFloat(data.volume),
      lastUpdate: Date.now(),
    };
  } catch (error) {
    console.error(`Erreur lors du chargement du ticker pour ${symbol}:`, error);
    // Lancer l'erreur pour que l'appelant puisse la gérer
    throw error;
  }
};

/**
 * Récupère les données de chandeliers (candles) pour un symbole
 */
export const getCandles = async (
  symbol: string,
  timeframe: Timeframe = "1h",
  limit: number = 100,
): Promise<Candle[]> => {
  try {
    const normalized = normalizeSymbol(symbol);
    const intervalMap: Record<Timeframe, string> = {
      "1m": "1m",
      "5m": "5m",
      "15m": "15m",
      "1h": "1h",
      "4h": "4h",
      "1d": "1d",
    };

    const response = await fetch(
      `${BINANCE_API}/klines?symbol=${normalized}&interval=${intervalMap[timeframe]}&limit=${limit}`,
    );

    if (!response.ok) {
      throw new Error(`Erreur API: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();

    // Vérifier si Binance retourne une erreur
    if (data.code) {
      throw new Error(data.msg || "Symbole non disponible sur Binance");
    }

    if (Array.isArray(data) && data.length > 0 && Array.isArray(data[0])) {
      return data.map((kline: any[]) => ({
        timestamp: kline[0],
        open: parseFloat(kline[1]),
        high: parseFloat(kline[2]),
        low: parseFloat(kline[3]),
        close: parseFloat(kline[4]),
        volume: parseFloat(kline[5]),
      }));
    }

    throw new Error("Format de données invalide");
  } catch (error) {
    console.error("Erreur lors du chargement des chandeliers:", error);
    // Retourner des données de fallback simulées
    return generateMockCandles(limit);
  }
};

/**
 * Récupère le carnet d'ordres (order book) pour un symbole
 */
export const getOrderBook = async (
  symbol: string,
  limit: number = 20,
): Promise<OrderBook> => {
  try {
    const normalized = normalizeSymbol(symbol);
    const response = await fetch(
      `${BINANCE_API}/depth?symbol=${normalized}&limit=${limit}`,
    );

    if (!response.ok) {
      throw new Error(`Erreur API: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();

    if (data.code) {
      // Binance retourne une erreur dans le JSON
      throw new Error(data.msg || "Erreur API Binance");
    }

    return {
      bids: data.bids.map(([price, quantity]: [string, string]) => ({
        price: parseFloat(price),
        quantity: parseFloat(quantity),
      })),
      asks: data.asks.map(([price, quantity]: [string, string]) => ({
        price: parseFloat(price),
        quantity: parseFloat(quantity),
      })),
      lastUpdateId: data.lastUpdateId,
    };
  } catch (error) {
    console.error("Erreur lors du chargement du carnet d'ordres:", error);
    // Retourner des données de fallback simulées
    return generateMockOrderBook();
  }
};

/** Plage temporelle pour le graphique de la Vue marché en direct */
export type ChartRange = "1D" | "1M" | "3M" | "1Y" | "5Y" | "All";

/**
 * Récupère les chandeliers pour un graphique selon la plage choisie.
 * Utilisé par la section "Vue marché en direct" (Market Analysis & Insights).
 */
export const getChartCandles = async (
  symbol: string,
  range: ChartRange,
): Promise<Array<{ time: string; value: number }>> => {
  const map: Record<ChartRange, { interval: string; limit: number }> = {
    "1D": { interval: "15m", limit: 96 },
    "1M": { interval: "1d", limit: 30 },
    "3M": { interval: "1d", limit: 90 },
    "1Y": { interval: "1d", limit: 365 },
    "5Y": { interval: "1w", limit: 260 },
    All: { interval: "1w", limit: 500 },
  };

  const { interval, limit } = map[range];
  const normalized = normalizeSymbol(symbol);

  try {
    const response = await fetch(
      `${BINANCE_API}/klines?symbol=${normalized}&interval=${interval}&limit=${limit}`,
    );

    if (!response.ok) {
      throw new Error(`Erreur API: ${response.status}`);
    }

    const data = await response.json();

    if (data.code) {
      throw new Error(data.msg || "Symbole non disponible");
    }

    if (!Array.isArray(data) || data.length === 0) {
      return [];
    }

    const locale =
      typeof navigator !== "undefined" ? navigator.language : "fr-FR";
    return data.map((k: number[]) => {
      const timestamp = k[0];
      const close = parseFloat(String(k[4]));
      const date = new Date(timestamp);
      const time =
        range === "1D"
          ? date.toLocaleTimeString(locale, {
              hour: "2-digit",
              minute: "2-digit",
            })
          : range === "1M" || range === "3M"
          ? date.toLocaleDateString(locale, { day: "numeric", month: "short" })
          : date.toLocaleDateString(locale, {
              month: "short",
              year: "2-digit",
            });
      return { time, value: close };
    });
  } catch (error) {
    console.warn(`Erreur getChartCandles pour ${symbol}:`, error);
    return [];
  }
};

/**
 * Génère des chandeliers simulés pour le développement
 */
const generateMockCandles = (count: number): Candle[] => {
  const candles: Candle[] = [];
  let basePrice = 45000;
  const now = Date.now();

  for (let i = count - 1; i >= 0; i--) {
    const change = (Math.random() - 0.5) * 1000;
    const open = basePrice;
    const close = basePrice + change;
    const high = Math.max(open, close) + Math.random() * 200;
    const low = Math.min(open, close) - Math.random() * 200;
    const volume = Math.random() * 1000000;

    candles.push({
      timestamp: now - i * 3600000, // 1 heure par chandelier
      open,
      high,
      low,
      close,
      volume,
    });

    basePrice = close;
  }

  return candles;
};

/**
 * Génère un carnet d'ordres simulé pour le développement
 */
const generateMockOrderBook = (): OrderBook => {
  const basePrice = 45000;
  const bids: OrderBookEntry[] = [];
  const asks: OrderBookEntry[] = [];

  // Générer des ordres d'achat (bids) en dessous du prix actuel
  for (let i = 0; i < 10; i++) {
    bids.push({
      price: basePrice - (i + 1) * 10 - Math.random() * 5,
      quantity: Math.random() * 10 + 1,
    });
  }

  // Générer des ordres de vente (asks) au-dessus du prix actuel
  for (let i = 0; i < 10; i++) {
    asks.push({
      price: basePrice + (i + 1) * 10 + Math.random() * 5,
      quantity: Math.random() * 10 + 1,
    });
  }

  return {
    bids: bids.sort((a, b) => b.price - a.price), // Tri décroissant
    asks: asks.sort((a, b) => a.price - b.price), // Tri croissant
    lastUpdateId: Date.now(),
  };
};

/**
 * Crée une connexion WebSocket pour les données en temps réel
 */
export const createMarketWebSocket = (
  symbol: string,
  onMessage: (data: any) => void,
): WebSocket => {
  const normalized = normalizeSymbol(symbol);
  const stream = `${normalized.toLowerCase()}@ticker`;
  const ws = new WebSocket(`${BINANCE_WS}/${stream}`);

  ws.onmessage = (event) => {
    try {
      const data = JSON.parse(event.data);
      onMessage(data);
    } catch (error) {
      console.error("Erreur lors du parsing des données WebSocket:", error);
    }
  };

  ws.onerror = (error) => {
    console.error("Erreur WebSocket:", error);
  };

  return ws;
};
